import { useLayoutEffect, useRef } from 'react';

export const useInfiniteScroll = <TLoader = undefined, TContainer = undefined>({
  fetchMore,
  hasMore,
  distance = 100,
  axis = 'vertical',
}: {
  fetchMore: () => void;
  hasMore: boolean;
  distance?: number;
  axis?: 'vertical' | 'horizontal';
}) => {
  const scrollContainerRef = useRef<TContainer>();
  const loaderRef = useRef<TLoader>();
  const isIntersecting = useRef(false);

  useLayoutEffect(() => {
    const loaderNode = loaderRef.current as Element | undefined;
    const scrollContainerNode = scrollContainerRef.current as Element | undefined;
    if (!loaderNode || !hasMore) {
      return;
    }

    const options = {
      root: scrollContainerNode ?? null,
      rootMargin: axis === 'horizontal' ? `0px ${distance}px 0px 0px` : `0px 0px ${distance}px 0px`,
    };

    const listener = (entries: IntersectionObserverEntry[]) => {
      if (entries[entries.length - 1]?.isIntersecting && !isIntersecting.current) {
        isIntersecting.current = true;
        fetchMore();
      }
      if (!entries[entries.length - 1]?.isIntersecting && isIntersecting.current) {
        isIntersecting.current = false;
      }
    };

    const observer = new IntersectionObserver(listener, options);
    observer.observe(loaderNode);

    return () => observer.disconnect();
  }, [hasMore, distance, axis, fetchMore]);

  return {
    loaderRef,
    scrollContainerRef,
  };
};
